import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';
import Box from '@mui/material/Box';
import {
  Alert,
  Grid, IconButton, Stack, Typography,
} from '@mui/material';
import api from '../../../services/api';
import {ServiceReservationConfirmationCard} from "../components/ServiceReservationConfirmationCard";
import {Table} from "../components/Table";
import ReactDOM from "react-dom";
import {useGridApiRef} from "@mui/x-data-grid";
import {useStateContext} from "../../../hooks/useStateContext";
import {ChatDialog} from "../../../modules/accommodation-provider/components/ChatDialog";
import {LoadingButton} from "@mui/lab";
import toast from "react-hot-toast";
import {ConfirmedList} from "../components/ConfirmedList";
import {AssignmentTurnedInRounded, HourglassBottomRounded} from "@mui/icons-material";
import {SkeletonCard} from "../components/SkeletonCard";
import {useNavigate, useParams} from "react-router-dom";
import {ServiceReservationSummary} from "../../../types/ServiceReservationSummary";
import {ConfirmConfirmationDialog} from "../components/ConfirmConfirmationDialog";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import Tooltip from "@mui/material/Tooltip";

interface Props {
  data: any,
}

export const ViewPage: React.ComponentType<Props> = ({data}) => {
  const [confirmations, setConfirmations] = useState<any>([]);
  const [confirmedReservations, setConfirmedReservations] = useState<any>([]);
  const [loading, setLoading] = useState<any>(false);
  const [customFields, setCustomFields] = useState<any>([]);
  const [editableFields, setEditableFields] = useState<any>({});
  const [loadingConfirmed, setLoadingConfirmed] = useState<any>(false);
  const apiRef = useGridApiRef();
  const autosizeOptions = {
    includeHeaders: true,
    includeOutliers: true,
    outliersFactor: 1.5,
    expand: false,
  };

  const {user, isMobile} = useStateContext();
  const [openChat, setOpenChat] = useState<any>(false);
  const [showConfirmAll, setShowConfirmAll] = useState<any>(false);
  const [showConfirmAllLoading, setShowConfirmAllLoading] = useState<any>(false);

  const {id, action} = useParams();
  const navigate = useNavigate();
  const [confirmation, setConfirmation] = useState<ServiceReservationSummary | undefined | true>();
  const [loadingConfirmation, setLoadingConfirmation] = useState<boolean>(false);

  const {serviceReservationId} = data;

  const fetchServiceReservationConfirmations = useCallback(async () => {
    setLoading(true);
    await api
      .get(`/accommodation-provider/accommodation-reservation-confirmations`)
      .then((result: any) => {
        setConfirmations(result);
        setLoading(false);
      })
      .catch((error) => {
      });
  }, []);

  const fetchConfirmedServiceReservations = useCallback(async (tab?: any) => {
    setLoadingConfirmed(true);

    const searchParams = new URLSearchParams({});

    if (tab) {
      searchParams.set('status', (tab === 1 ? 'past' : 'cancelled'));
    }

    await api
      .get(`/accommodation-provider/accommodation-reservation-confirmations/confirmed`, searchParams)
      .then((res: any) => {
        api.get(`/custom-fields/${user.id}`).then((result: any) => {
          setCustomFields(result?.custom_fields);
          setEditableFields(result?.editable_fields);
          setConfirmedReservations(res);
          setLoadingConfirmed(false);
          ReactDOM.flushSync(() => {
            if (!isMobile && apiRef?.current) {
              apiRef.current.updateRows(res);
            }
          });
        })
          .then(() => sleep(0))
          .then(() => {
            if (!isMobile && apiRef?.current) {
              apiRef.current.autosizeColumns(autosizeOptions);
            }
          });
      })

      .catch((e) => {
        console.log(e);
      });
  }, []);

  const fetchUserData = useCallback(() => {
    api.get(`/service-provider/users/${user.id}`).then((res: any) => {

      let showProviderConfirmAll = false;
      res.service_providers.map((provider: any) => {
        if (provider?.confirm_all) {
          showProviderConfirmAll = true;
        }
      });
      setShowConfirmAll(showProviderConfirmAll);
    });
  }, []);

  const submitConfirmAll = useCallback(async () => {
    setLoading(true);
    setShowConfirmAllLoading(true);
    await api
      .post(`/service-provider/service-reservation-confirmations/confirm-all`, {})
      .then((result: any) => {
        toast.success('Az összes foglalás elfogadásra került');
      })
      .catch((error: any) => {
        toast.error('Hiba az foglalás során');
      })
      .finally(() => {
        setLoading(false);
        setShowConfirmAllLoading(false);
        fetchServiceReservationConfirmations();
        fetchConfirmedServiceReservations();
      });
  }, []);

  useEffect(() => {
    fetchServiceReservationConfirmations();
    fetchConfirmedServiceReservations();
    fetchUserData();
  }, []);

  useEffect(() => {

    if (!serviceReservationId) {
      return;
    }

    api
      .get(`/service-provider/service-reservation-confirmations/${serviceReservationId}`)
      .then((res: any) => {

        const resData = {...res};
        resData.formatted.id = res.service_reservation_id;

        setOpenChat(resData);
      })
      .catch((e) => {
        console.log(e);
      });

  }, [serviceReservationId]);

  useEffect(() => {
    if (id && action) {
      setConfirmation(undefined);
      setLoadingConfirmation(true);

      api
        .patch<unknown>(
          `/service-provider/service-reservation-confirmations/confirmed/summary/${id}`,
          {
            confirmation: action !== 'confirm' ? 0 : 1,
          }
        )
        .then((response) => {
          setConfirmation(parse(response));
          navigate('/', { replace: true });

        })
        .catch(()=>{
          toast.error('Sajnos nem sikerült visszaigazolni a foglalásokat, kérjük próbálja meg később.');
        })
        .finally(() => {
          setLoadingConfirmation(false);
        });
    }
  }, [id, action, navigate]);

  return (
    <>
      <Box
        sx={{
          width: '100%',
          height: '100vh',
          overflow: 'auto',
          padding: '10px'
        }}
      >
        <Box>
          <Stack
            gap={1}
            direction={'row'}
            sx={{
              marginBottom: '20px',
              '@media (max-width:600px)': {
                marginBottom: '10px',
              },
            }}
            alignItems={'center'}
          >
            <Typography
              color={'#666'}
              alignItems={'center'}
            >
              <HourglassBottomRounded color={'primary'}/> Folyamatban lévő lekérések
            </Typography>

            <Tooltip title={'Folyamatban lévő lekérések frissítése'}>
              <IconButton
                size={'small'}
                onClick={() => {
                  fetchServiceReservationConfirmations();
                }}
              >
                <RefreshRoundedIcon fontSize={'small'} color={'primary'} />
              </IconButton>
            </Tooltip>

            {showConfirmAll && (
              <LoadingButton
                loading={showConfirmAllLoading || loading}
                variant={'contained'}
                size={'small'}
                onClick={() => {
                  submitConfirmAll();
                }}
              >
                Összes foglalás elfogadása
              </LoadingButton>
            )}
          </Stack>

          <Grid container spacing={3}>
            {loading ? (
              Array.from({length: 3}).map((_, index) => (
                <Grid item xs={12} md={6} lg={4} xl={3} key={index}>
                  <SkeletonCard/>
                </Grid>
              ))
            ) : (
              <>
                {confirmations.length === 0 && (
                  <Grid item xs={12} md={12}>
                    <Alert severity="info">Jelenleg nincs folyamatban lévő lekérés</Alert>
                  </Grid>
                )}
                {confirmations.map((item: any) => {
                  return (
                    <Grid item xs={12} md={6} lg={6} xl={6}>
                      <ServiceReservationConfirmationCard
                        onRefresh={() => {
                          fetchServiceReservationConfirmations();
                          fetchConfirmedServiceReservations();
                        }}
                        item={item}
                        editableFields={editableFields}
                        onOpenChat={()=>{
                          const rowData = { ...item.formatted };
                          rowData.id = item?.accommodation_reservation_id;

                          setOpenChat({
                            formatted: rowData,
                            type: rowData?.formatted_type,
                          });
                        }}
                      />
                    </Grid>
                  );
                })}
              </>
            )}
          </Grid>
        </Box>
        <Box sx={{
          paddingTop: '20px'
        }}>
          <Stack direction={'row'} gap={1} alignItems={'center'} sx={{marginBottom: '10px'}}>
            <Typography color={'#666'}>
              <AssignmentTurnedInRounded color={'primary'}/> Megválaszolt lekérések
            </Typography>
            <Tooltip title={'Folyamatban lévő lekérések frissítése'}>
              <IconButton
                size={'small'}
                onClick={() => {
                  fetchConfirmedServiceReservations();
                }}
              >
                <RefreshRoundedIcon fontSize={'small'} color={'primary'} />
              </IconButton>
            </Tooltip>
          </Stack>
          <Box>
            {isMobile ? (
              <ConfirmedList
                apiRef={apiRef}
                onOpenChat={(value) => {
                  setOpenChat({
                    formatted: value,
                    type: value?.formatted_type,
                  });
                }}
                onLoading={(value) => {
                  setLoading(value);
                }}
                onChangeTab={(value) => {
                  fetchConfirmedServiceReservations(value);
                }}
                onRefresh={() => {
                  fetchConfirmedServiceReservations();
                  fetchServiceReservationConfirmations();
                }}
                rows={confirmedReservations}
                customFields={customFields}
                editableFields={editableFields}
                loading={loadingConfirmed}
              />
            ) : (
              <Table
                apiRef={apiRef}
                onOpenChat={(value) => {
                  setOpenChat({
                    formatted: value,
                    type: value?.formatted_type,
                  });
                }}
                onLoading={(value) => {
                  setLoading(value);
                }}
                onChangeTab={(value) => {
                  fetchConfirmedServiceReservations(value);
                }}
                onRefresh={() => {
                  fetchConfirmedServiceReservations();
                  fetchServiceReservationConfirmations();
                }}
                rows={confirmedReservations}
                customFields={customFields}
                editableFields={editableFields}
                loading={loadingConfirmed}
              />
            )}


          </Box>
        </Box>
        {openChat && (
          <ChatDialog
            data={openChat}
            onClose={() => setOpenChat(false)}
          />
        )}
        {confirmation && (
          <>
            <ConfirmConfirmationDialog
              onClose={() => {
                navigate("/", { replace: true });
                setConfirmation(undefined);
              }}
              confirmation={confirmation}
              loading={loadingConfirmed}
            />
          </>
        )}

      </Box>
    </>
  );
};

function sleep(ms: number) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

const parse = (response: unknown): ServiceReservationSummary => {
  if (
    response !== null &&
    typeof response === 'object' &&
    'id' in response &&
    typeof response.id === 'number' &&
    'filename' in response &&
    typeof response.filename === 'string' &&
    'confirmation' in response &&
    typeof response.confirmation === 'number' &&
    'created_at' in response &&
    typeof response.created_at === 'string' &&
    'updated_at' in response &&
    typeof response.updated_at === 'string' &&
    'from' in response &&
    typeof response.from === 'string' &&
    'to' in response &&
    typeof response.to === 'string'
  ) {
    const a = response.id;
    return {
      id: response.id,
      filename: response.filename,
      confirmation: response.confirmation !== 0,
      createdAt: new Date(response.created_at),
      updatedAt: new Date(response.updated_at),
      from: new Date(response.from),
      to: new Date(response.to),
    };
  }

  throw new Error();
};
